/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Home, RotateCcw, Volume2, VolumeX, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { savePlayerNames, loadPlayerNames } from "@/utils/playerStorage";
import { recordGameWon } from "@/utils/playerProfile";

import { getVoiceLang } from "@/utils/voicePref";
const speakText = (t) => {
  try { if (!window.speechSynthesis) return; window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t); u.lang = getVoiceLang(); u.rate=0.85; u.pitch=1.0; window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("game op failed", err); }
};

const TangkapBintang = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [gameState, setGameState] = useState("menu");
  const [playerNames, setPlayerNames] = useState(() => { const s = loadPlayerNames(); return [s[0]||"",s[1]||""]; });
  const [scores, setScores] = useState([0, 0]);
  const [basket1X, setBasket1X] = useState(25);
  const [basket2X, setBasket2X] = useState(75);
  const [fallingItems, setFallingItems] = useState([]);
  const [timeLeft, setTimeLeft] = useState(30);
  const [soundOn, setSoundOn] = useState(true);
  const [gameOver, setGameOver] = useState(false);
  const rafRef = useRef(null);
  const spawnRef = useRef(null);
  const timerRef = useRef(null);
  let itemId = useRef(0);

  const p1 = playerNames[0] || "Pemain 1";
  const p2 = playerNames[1] || "Pemain 2";

  const startGame = () => {
    savePlayerNames(playerNames);
    setScores([0, 0]); setBasket1X(25); setBasket2X(75);
    setFallingItems([]); setTimeLeft(30); setGameOver(false);
    setGameState("playing");
  };

  // Spawn items
  useEffect(() => {
    if (gameState !== "playing" || gameOver) return;
    spawnRef.current = setInterval(() => {
      const isBomb = Math.random() < 0.2;
      itemId.current += 1;
      setFallingItems(prev => [...prev, {
        id: itemId.current,
        x: 5 + Math.random() * 90,
        y: -5,
        type: isBomb ? "bomb" : "star",
        emoji: isBomb ? "💣" : ["⭐", "🌟", "✨"][Math.floor(Math.random() * 3)],
        speed: 0.4 + Math.random() * 0.3,
      }]);
    }, 600);
    return () => clearInterval(spawnRef.current);
  }, [gameState, gameOver]);

  // Timer
  useEffect(() => {
    if (gameState !== "playing" || gameOver) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setGameOver(true);
          recordGameWon("tangkap");
          const winner = scores[0] > scores[1] ? p1 : scores[1] > scores[0] ? p2 : "Seri";
          if (soundOn) speakText(`Time's up! ${winner === "Draw" ? "Draw!" : winner + " wins!"}`);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [gameState, gameOver]);

  // Game loop - falling items
  useEffect(() => {
    if (gameState !== "playing" || gameOver) return;
    const loop = () => {
      setFallingItems(prev => {
        const alive = [];
        prev.forEach(item => {
          const newY = item.y + item.speed;
          // Check catch by basket1 (left half)
          if (newY >= 85 && newY < 95) {
            if (Math.abs(item.x - basket1X) < 12) {
              if (item.type === "star") setScores(s => [s[0] + 1, s[1]]);
              else setScores(s => [Math.max(0, s[0] - 2), s[1]]);
              return;
            }
            if (Math.abs(item.x - basket2X) < 12) {
              if (item.type === "star") setScores(s => [s[0], s[1] + 1]);
              else setScores(s => [s[0], Math.max(0, s[1] - 2)]);
              return;
            }
          }
          if (newY < 105) alive.push({ ...item, y: newY });
        });
        return alive;
      });
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [gameState, gameOver, basket1X, basket2X]);

  // Keyboard controls
  useEffect(() => {
    if (gameState !== "playing" || gameOver) return;
    const handle = (e) => {
      if (e.key === 'a' || e.key === 'A') setBasket1X(prev => Math.max(8, prev - 5));
      if (e.key === 'd' || e.key === 'D') setBasket1X(prev => Math.min(45, prev + 5));
      if (e.key === 'j' || e.key === 'J') setBasket2X(prev => Math.max(55, prev - 5));
      if (e.key === 'l' || e.key === 'L') setBasket2X(prev => Math.min(92, prev + 5));
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [gameState, gameOver]);

  if (gameState === "menu") {
    return (
      <div className="flex flex-col items-center w-full max-w-2xl mx-auto px-4 pt-4" data-testid="tangkap-bintang-menu">
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="glass-card-strong rounded-2xl p-6 w-full text-center mb-4">
          <motion.span className="text-6xl block mb-3" animate={{ rotate: [0,10,-10,0] }} transition={{ duration: 2, repeat: Infinity }}>⭐</motion.span>
          <h1 className="font-fredoka text-3xl text-gold font-bold mb-2">Tangkap Bintang</h1>
          <p className="font-fredoka text-gold-light/80 text-sm mb-4">Tangkap bintang, hindari bom! 30 detik!</p>
          <div className="glass-card rounded-xl p-3 mb-3 text-left">
            <p className="font-fredoka text-gold-light/60 text-xs">{p1}: <span className="text-cyan-400 font-bold">A/D</span> geser kiri/kanan</p>
            <p className="font-fredoka text-gold-light/60 text-xs">{p2}: <span className="text-pink-400 font-bold">J/L</span> geser kiri/kanan</p>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <input data-testid="tangkap-p1" type="text" value={playerNames[0]} onChange={e => setPlayerNames([e.target.value, playerNames[1]])} placeholder="Pemain 1" maxLength={15} className="glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none" />
            <input data-testid="tangkap-p2" type="text" value={playerNames[1]} onChange={e => setPlayerNames([playerNames[0], e.target.value])} placeholder="Pemain 2" maxLength={15} className="glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none" />
          </div>
          <Button onClick={startGame} className="w-full h-12 font-fredoka font-bold text-lg rounded-xl bg-gradient-to-r from-yellow-400 to-amber-500" data-testid="tangkap-start">MULAI TANGKAP!</Button>
        </motion.div>
        <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold hover:bg-gold/10 font-fredoka rounded-xl"><Home className="w-4 h-4 mr-2" />Menu</Button>
      </div>
    );
  }

  const containerH = Math.min(winH - 160, 450);

  return (
    <div className="flex flex-col items-center w-full max-w-3xl mx-auto px-2" data-testid="tangkap-bintang-game">
      {gameOver && <Confetti width={winW} height={winH} recycle={false} numberOfPieces={200} />}
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="font-fredoka text-gold font-bold text-sm">⭐ ⏱️{timeLeft}s</span>
          <span className="font-fredoka text-cyan-400 text-xs font-bold">{p1}: {scores[0]}</span>
          <span className="font-fredoka text-pink-400 text-xs font-bold">{p2}: {scores[1]}</span>
        </div>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={() => setGameState("menu")} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      <div className="w-full rounded-2xl overflow-hidden relative border-2 border-yellow-400/30"
        style={{ height: containerH, background: "linear-gradient(180deg, #0f0c29 0%, #302b63 50%, #24243e 100%)" }}>
        {/* Falling items */}
        {fallingItems.map(item => (
          <div key={item.id} className="absolute text-2xl sm:text-3xl select-none z-10 transition-none"
            style={{ left: `${item.x}%`, top: `${item.y}%`, transform: 'translate(-50%, -50%)' }}>
            {item.emoji}
          </div>
        ))}

        {/* Basket 1 */}
        <div className="absolute text-3xl sm:text-4xl select-none z-20"
          style={{ left: `${basket1X}%`, bottom: '5%', transform: 'translateX(-50%)' }}>
          🧺
          <span className="absolute -top-5 left-1/2 -translate-x-1/2 font-fredoka text-[10px] text-cyan-400 font-bold whitespace-nowrap">{p1}</span>
        </div>

        {/* Basket 2 */}
        <div className="absolute text-3xl sm:text-4xl select-none z-20"
          style={{ left: `${basket2X}%`, bottom: '5%', transform: 'translateX(-50%)' }}>
          🧺
          <span className="absolute -top-5 left-1/2 -translate-x-1/2 font-fredoka text-[10px] text-pink-400 font-bold whitespace-nowrap">{p2}</span>
        </div>

        {/* Divider line */}
        <div className="absolute top-0 bottom-0 left-1/2 w-0.5 bg-white/10" />

        {/* Game over */}
        {gameOver && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 flex items-center justify-center z-30 bg-black/60">
            <div className="glass-card-strong rounded-2xl p-6 text-center">
              <span className="text-5xl block mb-2">🏆</span>
              <h2 className="font-fredoka text-2xl text-gold font-bold mb-1">
                {scores[0] > scores[1] ? `${p1} Menang!` : scores[1] > scores[0] ? `${p2} Menang!` : "Seri!"}
              </h2>
              <p className="font-fredoka text-gold-light/80 text-sm mb-3">{p1}: {scores[0]} ⭐ | {p2}: {scores[1]} ⭐</p>
              <div className="flex flex-col gap-2">
                <Button onClick={startGame} className="w-full font-fredoka font-bold rounded-xl bg-gradient-to-r from-yellow-400 to-amber-500"><RotateCcw className="w-4 h-4 mr-2" />Main Lagi</Button>
                <Button onClick={() => setGameState("menu")} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl">Menu</Button>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Mobile controls */}
      <div className="flex gap-2 w-full mt-2">
        <Button onClick={() => setBasket1X(prev => Math.max(8, prev - 8))} className="flex-1 h-12 font-fredoka font-bold rounded-xl bg-cyan-500 btn-3d text-sm" style={{ touchAction: "manipulation" }}>◀ {p1}</Button>
        <Button onClick={() => setBasket1X(prev => Math.min(45, prev + 8))} className="flex-1 h-12 font-fredoka font-bold rounded-xl bg-cyan-600 btn-3d text-sm" style={{ touchAction: "manipulation" }}>{p1} ▶</Button>
        <Button onClick={() => setBasket2X(prev => Math.max(55, prev - 8))} className="flex-1 h-12 font-fredoka font-bold rounded-xl bg-pink-500 btn-3d text-sm" style={{ touchAction: "manipulation" }}>◀ {p2}</Button>
        <Button onClick={() => setBasket2X(prev => Math.min(92, prev + 8))} className="flex-1 h-12 font-fredoka font-bold rounded-xl bg-pink-600 btn-3d text-sm" style={{ touchAction: "manipulation" }}>{p2} ▶</Button>
      </div>
    </div>
  );
};

export default TangkapBintang;
